import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Container,
  Card,
  Form,
  Button,
  Alert,
  Image,
  Spinner,
  Modal,
} from "react-bootstrap";
import { FaFacebook } from "react-icons/fa";
import {
  memberService,
  authService,
  subMemberService,
  facebookService,
} from "../../services/api";
import { useNavigate } from "react-router-dom";

const MyProfile = () => {
  const navigate = useNavigate();

  // State for form data
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    username: "",
    page: "",
  });

  // State for sub-member management
  const [isSubMember, setIsSubMember] = useState(false);
  const [subMembers, setSubMembers] = useState([]);
  const [subMemberFormData, setSubMemberFormData] = useState({
    id: "",
    username: "",
    firstName: "",
    lastName: "",
    email: "",
    password: "",
  });
  const [showSubMemberModal, setShowSubMemberModal] = useState(false);
  const [subMemberValidated, setSubMemberValidated] = useState(false);

  // State for alerts and validation
  const [validated, setValidated] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertVariant, setAlertVariant] = useState("success");
  const [alertMessage, setAlertMessage] = useState("");

  // State for Facebook connection
  const [facebookConnected, setFacebookConnected] = useState(false);
  const [facebookUserData, setFacebookUserData] = useState(null);
  const [acceptFacebookTerms, setAcceptFacebookTerms] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isProfileLoading, setIsProfileLoading] = useState(true);
  const [facebookLoginWindow, setFacebookLoginWindow] = useState(null);
  const [facebookLoginInterval, setFacebookLoginInterval] = useState(null);

  // Check authentication and load profile data
  useEffect(() => {
    // Check if user is authenticated
    if (!authService.isAuthenticated()) {
      navigate("/login");
      return;
    }

    // Fetch user profile
    const fetchUserProfile = async () => {
      try {
        const response = await memberService.getProfile();
        const profileData = response.data;

        setFormData({
          firstName: profileData.firstName || "",
          lastName: profileData.lastName || "",
          email: profileData.email || "",
          username: profileData.username || "",
          page: profileData.page || "",
        });

        // Check if user is a sub-member
        const isSubAcc = subMemberService.isSubMember();
        setIsSubMember(isSubAcc);

        // If not a sub-member, fetch sub-members
        if (!isSubAcc) {
          try {
            const subMembersResponse = await subMemberService.getSubMembers();
            setSubMembers(subMembersResponse.data || []);
          } catch (subError) {
            console.error("Failed to load sub-members", subError);
          }
        }

        // Check if Facebook is connected
        try {
          const fbResponse = await facebookService.getConnection();
          console.log("Facebook connection response:", fbResponse);
          if (fbResponse.facebookId) {
            setFacebookConnected(true);
            setFacebookUserData({
              name: fbResponse.name,
              email: fbResponse.email,
              picture: {
                data: {
                  url: fbResponse.profilePictureUrl,
                },
              },
            });
          }
        } catch (fbError) {
          console.error("Error checking Facebook connection:", fbError);
          // Facebook not connected, ignore error
        }
      } catch (error) {
        setAlertVariant("danger");
        setAlertMessage("Failed to load profile data. Please try again.");
        setShowAlert(true);

        // If unauthorized, redirect to login
        if (error.response && error.response.status === 401) {
          authService.logout();
          navigate("/login");
        }
      } finally {
        setIsProfileLoading(false);
      }
    };

    fetchUserProfile();

    // Cleanup function for Facebook login resources
    return () => {
      // Clear any Facebook login intervals if they exist
      if (facebookLoginInterval) {
        clearInterval(facebookLoginInterval);
      }

      // Close any Facebook login windows if they exist
      if (facebookLoginWindow && !facebookLoginWindow.closed) {
        facebookLoginWindow.close();
      }
    };
  }, [navigate, facebookLoginInterval, facebookLoginWindow]);

  // Fetch Facebook user data from our API
  const fetchFacebookUserData = async () => {
    try {
      // Get Facebook connection details from our API
      const connectionData = await facebookService.getConnection();

      if (connectionData && connectionData.facebookId) {
        // Update the Facebook user data state
        setFacebookUserData({
          name: connectionData.name,
          email: connectionData.email || "",
          picture: {
            data: {
              url: connectionData.profilePictureUrl || "",
            },
          },
        });

        // Set Facebook connected state
        setFacebookConnected(true);

        // Optionally update user profile with Facebook data
        if (connectionData.name) {
          const nameParts = connectionData.name.split(" ");
          const firstName = nameParts[0] || "";
          const lastName =
            nameParts.length > 1 ? nameParts.slice(1).join(" ") : "";

          setFormData((prevData) => ({
            ...prevData,
            firstName: firstName || prevData.firstName,
            lastName: lastName || prevData.lastName,
            email: connectionData.email || prevData.email,
          }));
        }
      } else {
        setFacebookConnected(false);
        setFacebookUserData(null);
      }
    } catch (error) {
      console.error("Error fetching Facebook user data:", error);
      setFacebookConnected(false);
      setFacebookUserData(null);
      setAlertVariant("danger");
      setAlertMessage("Failed to fetch Facebook user data. Please try again.");
      setShowAlert(true);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // Handle Facebook login using manual flow
  const handleFacebookLogin = () => {
    setIsLoading(true);

    // Generate a random state parameter to prevent CSRF attacks
    const state = Math.random().toString(36).substring(2, 15);

    // Store the state in localStorage to verify when the user returns
    localStorage.setItem("facebookLoginState", state);

    // Facebook App ID from environment variable
    const appId = process.env.REACT_APP_FACEBOOK_APP_ID;

    // Redirect URI (must be whitelisted in your Facebook App settings)
    const redirectUri = `${window.location.origin}/facebook-callback`;
    // const redirectUri = `https://page-api.oomlott.cloud/api/v1/facebook/auth/callback`;

    // Permissions to request
    const scope =
      "public_profile,pages_messaging,pages_read_user_content,pages_read_engagement,pages_manage_metadata,page_events,business_management";

    // Construct the Facebook login URL
    const facebookLoginUrl = `https://www.facebook.com/v23.0/dialog/oauth?client_id=${appId}&redirect_uri=${encodeURIComponent(redirectUri)}&state=${state}&scope=${scope}&response_type=token`;

    // Open a popup window for Facebook login
    const width = 600;
    const height = 700;
    const left = window.innerWidth / 2 - width / 2;
    const top = window.innerHeight / 2 - height / 2;

    const loginWindow = window.open(
      facebookLoginUrl,
      "facebook-login-popup",
      `width=${width},height=${height},left=${left},top=${top}`,
    );

    setFacebookLoginWindow(loginWindow);

    // Set up an interval to check if the popup has been closed or redirected
    const checkLoginInterval = setInterval(() => {
      try {
        // Check if the window was closed
        if (loginWindow.closed) {
          clearInterval(checkLoginInterval);
          setFacebookLoginInterval(null);
          setIsLoading(false);
          return;
        }

        // Try to access the current URL of the popup
        // This will throw an error if the popup has been redirected to a different origin
        const currentUrl = loginWindow.location.href;

        // Check if the URL contains the access token
        if (currentUrl.includes("access_token=")) {
          clearInterval(checkLoginInterval);
          setFacebookLoginInterval(null);

          // Parse the URL to extract the access token and other parameters
          const hashParams = new URLSearchParams(
            currentUrl.substring(currentUrl.indexOf("#") + 1),
          );

          const accessToken = hashParams.get("access_token");
          const expiresIn = hashParams.get("expires_in");
          const receivedState = hashParams.get("state");

          // Verify the state parameter to prevent CSRF attacks
          const originalState = localStorage.getItem("facebookLoginState");

          if (receivedState !== originalState) {
            setAlertVariant("danger");
            setAlertMessage("Security validation failed. Please try again.");
            setShowAlert(true);
            setIsLoading(false);
            loginWindow.close();
            return;
          }

          // Clear the state from localStorage
          localStorage.removeItem("facebookLoginState");

          // Get the Facebook user ID
          fetch(
            `https://graph.facebook.com/v23.0/me?access_token=${accessToken}`,
          )
            .then((response) => response.json())
            .then((userData) => {
              // Connect Facebook account to member profile
              const fbData = {
                facebookId: userData.id,
                accessToken: accessToken,
                expiresIn: parseInt(expiresIn, 10),
              };

              return facebookService.connect(fbData);
            })
            .then((response) => {
              // Set Facebook connected state
              setFacebookConnected(true);

              // Fetch updated Facebook user data from our API
              return fetchFacebookUserData();
            })
            .then(() => {
              setAlertVariant("success");
              setAlertMessage("Successfully connected to Facebook!");
              setShowAlert(true);
            })
            .catch((error) => {
              console.error("Facebook connection error:", error);
              setAlertVariant("danger");

              if (
                error.response &&
                error.response.data &&
                error.response.data.message
              ) {
                setAlertMessage(error.response.data.message);
              } else {
                setAlertMessage(
                  "Failed to connect to Facebook. Please try again.",
                );
              }

              setShowAlert(true);
            })
            .finally(() => {
              setIsLoading(false);
              loginWindow.close();
            });
        }
      } catch (error) {
        // An error will be thrown when the popup is redirected to a different origin
        // This is expected and we can ignore it
      }
    }, 500);

    setFacebookLoginInterval(checkLoginInterval);
  };

  // Handle Facebook logout
  const handleFacebookLogout = async () => {
    setIsLoading(true);

    try {
      // Get the current Facebook connection details to get the access token
      const connectionData = await facebookService.getConnection();

      if (connectionData && connectionData.accessToken) {
        // Construct the Facebook logout URL with the access token
        const facebookLogoutUrl = `https://graph.facebook.com/v23.0/me/permissions?access_token=${connectionData.accessToken}&method=delete`;

        // Make a direct request to revoke permissions
        try {
          const response = await fetch(facebookLogoutUrl);
          const result = await response.json();
          console.log("Facebook permissions revoked:", result);
        } catch (fbError) {
          console.error("Error revoking Facebook permissions:", fbError);
          // Continue with disconnection even if this fails
        }
      }

      // Disconnect from our backend
      const response = await facebookService.disconnect();

      // Clear any Facebook login intervals if they exist
      if (facebookLoginInterval) {
        clearInterval(facebookLoginInterval);
        setFacebookLoginInterval(null);
      }

      // Close any Facebook login windows if they exist
      if (facebookLoginWindow && !facebookLoginWindow.closed) {
        facebookLoginWindow.close();
        setFacebookLoginWindow(null);
      }

      // Reset Facebook connection state
      setFacebookConnected(false);
      setFacebookUserData(null);

      // Show success message
      setAlertVariant("success");
      setAlertMessage("Successfully disconnected from Facebook.");
      setShowAlert(true);

      // Scroll to top to show the alert
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      console.error("Facebook disconnect error:", error);
      setAlertVariant("danger");

      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        setAlertMessage(error.response.data.message);
      } else {
        setAlertMessage(
          "Failed to disconnect from Facebook. Please try again.",
        );
      }

      setShowAlert(true);

      // Scroll to top to show the alert
      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const form = e.currentTarget;
    if (form.checkValidity() === false) {
      e.stopPropagation();
      setValidated(true);
      return;
    }

    setIsLoading(true);

    try {
      await memberService.updateProfile({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
      });

      setAlertVariant("success");
      setAlertMessage("Profile updated successfully!");
      setShowAlert(true);

      // Scroll to top to show the alert
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      setAlertVariant("danger");

      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        setAlertMessage(error.response.data.message);
      } else {
        setAlertMessage("Failed to update profile. Please try again.");
      }

      setShowAlert(true);

      // Scroll to top to show the alert
      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      setIsLoading(false);
    }
  };

  // Sub-member management functions
  const handleSubMemberChange = (e) => {
    const { name, value } = e.target;
    setSubMemberFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubMemberSubmit = async (e) => {
    e.preventDefault();

    const form = e.currentTarget;
    if (form.checkValidity() === false) {
      e.stopPropagation();
      setSubMemberValidated(true);
      return;
    }

    setIsLoading(true);

    try {
      if (subMemberFormData.id) {
        // Update existing sub-member
        await subMemberService.updateSubMember(subMemberFormData.id, {
          firstName: subMemberFormData.firstName,
          lastName: subMemberFormData.lastName,
          email: subMemberFormData.email,
          ...(subMemberFormData.password
            ? { password: subMemberFormData.password }
            : {}),
        });
      } else {
        // Create new sub-member
        await subMemberService.createSubMember({
          username: subMemberFormData.username,
          firstName: subMemberFormData.firstName,
          lastName: subMemberFormData.lastName,
          email: subMemberFormData.email,
          password: subMemberFormData.password,
        });
      }

      // Refresh sub-members list
      const response = await subMemberService.getSubMembers();
      setSubMembers(response.data || []);

      // Close modal and reset form
      setShowSubMemberModal(false);
      setSubMemberFormData({
        id: "",
        username: "",
        firstName: "",
        lastName: "",
        email: "",
        password: "",
      });
      setSubMemberValidated(false);

      setAlertVariant("success");
      setAlertMessage(
        subMemberFormData.id
          ? "Sub-member updated successfully!"
          : "Sub-member created successfully!",
      );
      setShowAlert(true);

      // Scroll to top to show the alert
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      setAlertVariant("danger");

      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        setAlertMessage(error.response.data.message);
      } else {
        setAlertMessage("Failed to save sub-member. Please try again.");
      }

      setShowAlert(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteSubMember = async (subMemberId) => {
    if (
      window.confirm(
        "Are you sure you want to delete this sub-member? This action cannot be undone.",
      )
    ) {
      setIsLoading(true);

      try {
        await subMemberService.deleteSubMember(subMemberId);

        // Refresh sub-members list
        const response = await subMemberService.getSubMembers();
        setSubMembers(response.data || []);

        setAlertVariant("success");
        setAlertMessage("Sub-member deleted successfully!");
        setShowAlert(true);

        // Scroll to top to show the alert
        window.scrollTo({ top: 0, behavior: "smooth" });
      } catch (error) {
        setAlertVariant("danger");

        if (
          error.response &&
          error.response.data &&
          error.response.data.message
        ) {
          setAlertMessage(error.response.data.message);
        } else {
          setAlertMessage("Failed to delete sub-member. Please try again.");
        }

        setShowAlert(true);
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <div id="content-page" className="content-inner">
      <Container>
        <Row className="row-eq-height">
          <Col md="12">
            <Row className="mb-5">
              <Col md="12">
                <div className="card-header d-flex justify-content-between">
                  <div className="header-title">
                    <h4 className="card-title">My Profile</h4>
                  </div>
                </div>
              </Col>
            </Row>

            {showAlert && (
              <Alert
                variant={alertVariant}
                onClose={() => setShowAlert(false)}
                dismissible
              >
                {alertMessage}
              </Alert>
            )}

            <Row>
              <Col lg="6">
                <Card>
                  <Card.Header>
                    <h4 className="card-title">Basic Information</h4>
                  </Card.Header>
                  <Card.Body>
                    <Form
                      noValidate
                      validated={validated}
                      onSubmit={handleSubmit}
                    >
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3" controlId="formUsername">
                            <Form.Label>Username</Form.Label>
                            <Form.Control
                              type="text"
                              value={formData.username}
                              disabled
                              readOnly
                            />
                            <Form.Text className="text-muted">
                              Username cannot be changed
                            </Form.Text>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3" controlId="formPage">
                            <Form.Label>Facebook Page</Form.Label>
                            <Form.Control
                              type="text"
                              value={formData.page}
                              disabled
                              readOnly
                            />
                            <Form.Text className="text-muted">
                              Connected Facebook page
                            </Form.Text>
                          </Form.Group>
                        </Col>
                      </Row>

                      <Row>
                        <Col md={6}>
                          <Form.Group
                            className="mb-3"
                            controlId="formFirstName"
                          >
                            <Form.Label>First Name</Form.Label>
                            <Form.Control
                              type="text"
                              placeholder="Enter your first name"
                              name="firstName"
                              value={formData.firstName}
                              onChange={handleChange}
                              required
                            />
                            <Form.Control.Feedback type="invalid">
                              Please provide your first name.
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3" controlId="formLastName">
                            <Form.Label>Last Name</Form.Label>
                            <Form.Control
                              type="text"
                              placeholder="Enter your last name"
                              name="lastName"
                              value={formData.lastName}
                              onChange={handleChange}
                              required
                            />
                            <Form.Control.Feedback type="invalid">
                              Please provide your last name.
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                      </Row>

                      <Form.Group className="mb-3" controlId="formEmail">
                        <Form.Label>Email</Form.Label>
                        <Form.Control
                          type="email"
                          placeholder="Enter email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                        />
                        <Form.Control.Feedback type="invalid">
                          Please provide a valid email.
                        </Form.Control.Feedback>
                        <Form.Text className="text-muted">
                          We'll never share your email with anyone else.
                        </Form.Text>
                      </Form.Group>

                      <Form.Group className="mb-3" controlId="formCategory">
                        <Form.Label>Connect to Facebook</Form.Label>
                        {facebookConnected && facebookUserData ? (
                          <div className="d-flex align-items-center mb-3">
                            {facebookUserData.picture && (
                              <Image
                                src={facebookUserData.picture.data.url}
                                roundedCircle
                                width={50}
                                height={50}
                                className="me-3"
                              />
                            )}
                            <div>
                              <p className="mb-0">
                                <strong>{facebookUserData.name}</strong>
                              </p>
                              <p className="text-muted mb-0">
                                {facebookUserData.email ||
                                  "Email not available"}
                              </p>
                            </div>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              className="ms-auto"
                              onClick={handleFacebookLogout}
                            >
                              Disconnect
                            </Button>
                          </div>
                        ) : (
                          <>
                            <Form.Group
                              className="mb-3"
                              controlId="acceptFacebookTerms"
                            >
                              <Form.Check
                                type="checkbox"
                                id="accept_term"
                                checked={acceptFacebookTerms}
                                onChange={(e) =>
                                  setAcceptFacebookTerms(e.target.checked)
                                }
                                label={
                                  <>
                                    Connecting to Facebook page requires access
                                    token generation to allow RuaySure to access
                                    order information and messaging. This token
                                    will not be shared with any other
                                    individuals or organizations.
                                  </>
                                }
                              />
                            </Form.Group>
                            <Button
                              variant="primary"
                              className="d-flex align-items-center justify-content-center"
                              onClick={handleFacebookLogin}
                              disabled={!acceptFacebookTerms}
                            >
                              <FaFacebook className="me-2" size={20} />
                              Connect with Facebook
                            </Button>
                          </>
                        )}
                        <Form.Text className="text-muted">
                          Connect your Facebook account to import your profile
                          information.
                        </Form.Text>
                      </Form.Group>

                      <Button
                        variant="primary"
                        type="submit"
                        className="mt-2"
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <>
                            <Spinner
                              as="span"
                              animation="border"
                              size="sm"
                              role="status"
                              aria-hidden="true"
                              className="me-2"
                            />
                            Saving...
                          </>
                        ) : (
                          "Save Changes"
                        )}
                      </Button>
                    </Form>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Sub-member management section - only visible for main accounts */}
            {!isSubMember && (
              <Row className="mt-4">
                <Col lg="12">
                  <Card>
                    <Card.Header className="d-flex justify-content-between align-items-center">
                      <h4 className="card-title mb-0">Sub-Member Management</h4>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => setShowSubMemberModal(true)}
                      >
                        <span className="material-symbols-outlined me-1">
                          add
                        </span>
                        Add Sub-Member
                      </Button>
                    </Card.Header>
                    <Card.Body>
                      {subMembers.length > 0 ? (
                        <div className="table-responsive">
                          <table className="table table-striped">
                            <thead>
                              <tr>
                                <th>Username</th>
                                <th>Name</th>
                                <th>Email</th>
                                <th>Status</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {subMembers.map((member) => (
                                <tr key={member.id}>
                                  <td>{member.username}</td>
                                  <td>{`${member.firstName} ${member.lastName}`}</td>
                                  <td>{member.email}</td>
                                  <td>
                                    <span
                                      className={`badge bg-${member.isActive ? "success" : "danger"}`}
                                    >
                                      {member.isActive ? "Active" : "Inactive"}
                                    </span>
                                  </td>
                                  <td>
                                    <Button
                                      variant="outline-primary"
                                      size="sm"
                                      className="me-2"
                                      onClick={() => {
                                        setSubMemberFormData({
                                          id: member.id,
                                          username: member.username,
                                          firstName: member.firstName,
                                          lastName: member.lastName,
                                          email: member.email,
                                          password: "",
                                        });
                                        setShowSubMemberModal(true);
                                      }}
                                    >
                                      <span className="material-symbols-outlined">
                                        edit
                                      </span>
                                    </Button>
                                    <Button
                                      variant="outline-danger"
                                      size="sm"
                                      onClick={() =>
                                        handleDeleteSubMember(member.id)
                                      }
                                    >
                                      <span className="material-symbols-outlined">
                                        delete
                                      </span>
                                    </Button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="text-center py-4">
                          <p className="mb-0">
                            No sub-members found. Add a sub-member to allow
                            others to manage your Facebook page.
                          </p>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}
          </Col>
        </Row>
      </Container>

      {/* Sub-member Modal */}
      <Modal
        show={showSubMemberModal}
        onHide={() => {
          setShowSubMemberModal(false);
          setSubMemberFormData({
            id: "",
            username: "",
            firstName: "",
            lastName: "",
            email: "",
            password: "",
          });
          setSubMemberValidated(false);
        }}
        backdrop="static"
        keyboard={false}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {subMemberFormData.id ? "Edit Sub-Member" : "Add New Sub-Member"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form
            noValidate
            validated={subMemberValidated}
            onSubmit={handleSubMemberSubmit}
          >
            {!subMemberFormData.id && (
              <Form.Group className="mb-3" controlId="formSubUsername">
                <Form.Label>Username</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter username"
                  name="username"
                  value={subMemberFormData.username}
                  onChange={handleSubMemberChange}
                  disabled={!!subMemberFormData.id}
                  required
                />
                <Form.Control.Feedback type="invalid">
                  Please provide a username.
                </Form.Control.Feedback>
                <Form.Text className="text-muted">
                  Username cannot be changed after creation.
                </Form.Text>
              </Form.Group>
            )}

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="formSubFirstName">
                  <Form.Label>First Name</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter first name"
                    name="firstName"
                    value={subMemberFormData.firstName}
                    onChange={handleSubMemberChange}
                    required
                  />
                  <Form.Control.Feedback type="invalid">
                    Please provide a first name.
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="formSubLastName">
                  <Form.Label>Last Name</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter last name"
                    name="lastName"
                    value={subMemberFormData.lastName}
                    onChange={handleSubMemberChange}
                    required
                  />
                  <Form.Control.Feedback type="invalid">
                    Please provide a last name.
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3" controlId="formSubEmail">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                placeholder="Enter email"
                name="email"
                value={subMemberFormData.email}
                onChange={handleSubMemberChange}
                required
              />
              <Form.Control.Feedback type="invalid">
                Please provide a valid email.
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3" controlId="formSubPassword">
              <Form.Label>
                {subMemberFormData.id
                  ? "New Password (leave blank to keep current)"
                  : "Password"}
              </Form.Label>
              <Form.Control
                type="password"
                placeholder={
                  subMemberFormData.id ? "Enter new password" : "Enter password"
                }
                name="password"
                value={subMemberFormData.password}
                onChange={handleSubMemberChange}
                required={!subMemberFormData.id}
              />
              <Form.Control.Feedback type="invalid">
                {subMemberFormData.id ? "" : "Please provide a password."}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3" controlId="formSubConfirmPassword">
              <Form.Label>Confirm Password</Form.Label>
              <Form.Control
                type="password"
                placeholder="Confirm password"
                name="confirmPassword"
                value={subMemberFormData.confirmPassword}
                onChange={handleSubMemberChange}
                required={!subMemberFormData.id && subMemberFormData.password}
                isInvalid={
                  subMemberFormData.password !==
                    subMemberFormData.confirmPassword &&
                  subMemberFormData.confirmPassword !== ""
                }
              />
              <Form.Control.Feedback type="invalid">
                Password does not match.
              </Form.Control.Feedback>
            </Form.Group>

            <div className="d-flex justify-content-end mt-4">
              <Button
                variant="secondary"
                className="me-2"
                onClick={() => {
                  setShowSubMemberModal(false);
                  setSubMemberFormData({
                    id: "",
                    username: "",
                    firstName: "",
                    lastName: "",
                    email: "",
                    password: "",
                  });
                  setSubMemberValidated(false);
                }}
              >
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Saving...
                  </>
                ) : (
                  "Save"
                )}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default MyProfile;
